<template>
    <div id="app">
        <router-view v-if="isStandalonePage" />
        <template v-else>
            <div class="container-fluid">
                <div class="row" style="margin-bottom: 30px;">
                    <div class="col-md-12 col-sm-12">
                        <WeekGraph />
                    </div>
                </div>
            </div>
            <div class="container">
                <div class="row">
                    <div class="col-md-7">
                        <CityHeader :show-wiki="showWiki" @toggle-wiki="toggleWiki" />
                    </div>
                    <div class="col-md-5">
                        <CityTime />
                    </div>
                </div>
                <div v-if="showWiki" class="row">
                    <div class="col-md-12">
                        <div class="card w-100 border-0" style="width: 18rem; margin-bottom: 10px; margin-top: 10px; border-radius: 12px;">
                            <div class="card-body" style="background-color: rgba(25,135,84,0.15); border-radius: 12px;">
                                <div v-if="wikiLoading" class="text-muted small">Loading…</div>
                                <div v-else-if="wikiData" class="row g-0">
                                    <div class="col-12 col-md-5 pe-md-3">
                                        <img v-if="wikiData.thumbnail" :src="wikiData.thumbnail.source" :alt="wikiData.title"
                                             style="width:100%; border-radius:8px; object-fit:cover; max-height:260px;">
                                        <p v-if="wikiData.description" class="card-text small text-muted mt-1 mb-0">{{ wikiData.description }}</p>
                                    </div>
                                    <div class="col-12 col-md-7 text-start">
                                        <h3 class="h5 card-title mb-2">{{ wikiData.title }}<a v-if="wikiData.content_urls" :href="wikiData.content_urls.desktop.page"
                                               target="_blank" rel="noopener" class="btn btn-link btn-sm" style="font-size:12px; padding-left:4px; vertical-align:baseline;">Wikipedia ↗</a></h3>
                                        <p class="card-text small">{{ wikiData.extract }}</p>
                                        <p v-if="city.population || city.elevation" class="card-text small text-muted mb-0">
                                            <span v-if="city.population">Pop. {{ Number(city.population).toLocaleString() }}</span>
                                            <span v-if="city.population && city.elevation"> &nbsp;·&nbsp; </span>
                                            <span v-if="city.elevation">{{ city.elevation }} m</span>
                                        </p>
                                    </div>
                                </div>
                                <div v-else class="text-muted small">No Wikipedia article found for {{ city.name }}.</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12">
                        <Dashboard />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import CityTime from './components/ui/CityTime'
import CityHeader from './components/ui/CityHeader'
import WeekGraph from './components/ui/WeekGraph'
import Dashboard from './components/astro/Dashboard'
import locci from '@/api/locci'
import { getCookie, setCookie } from '@/utils/cookie'

const LOCATION_COOKIE = 'calendula_location'
const WIKI_COOKIE = 'calendula_wiki'
const DEFAULT_LAT = 48.4359
const DEFAULT_LON = -123.35155

export default {
    name: 'App',
    components: {
        Dashboard,
        CityTime,
        CityHeader,
        WeekGraph,
    },
    data() {
        return {
            showWiki: false,
            wikiData: null,
            wikiLoading: false,
            wikiCitySlug: null,
        }
    },
    computed: {
        ...mapState(['city']),
        isStandalonePage() { return ['api', 'journal-event'].includes(this.$route.name) },
    },
    watch: {
        city(newVal) {
            if (!newVal || !newVal.slug || newVal.slug === this.wikiCitySlug) return
            this.wikiData = null
            this.wikiLoading = false
            this.wikiCitySlug = newVal.slug
            if (this.showWiki) {
                this.wikiLoading = true
                this.fetchWiki(this.buildWikiCandidates(newVal))
            }
        },
        showWiki(val) { setCookie(WIKI_COOKIE, val) },
    },
    methods: {
        ...mapMutations(['setCity', 'setCityDatetime', 'setDate']),
        async loadCityFromCoords(lat, lon) {
            const r = await locci.findNearest(lat, lon)
            const city = r.data.results
            this.setCity(city)
            const dtR = await locci.fetchCityDatetime(city.slug)
            const cityDatetime = dtR.data.results
            this.setCityDatetime(cityDatetime)
            this.setDate(new Date(cityDatetime.current_year, cityDatetime.current_month - 1, cityDatetime.current_day))
            this.setCity({ ...city, timezone_slug: cityDatetime.timezone_slug })
        },
        async loadCityFromSlug(slug) {
            const r = await locci.fetchCity(slug)
            const { lat, lon } = r.data
            setCookie(LOCATION_COOKIE, `${lat},${lon}`)
            await this.loadCityFromCoords(lat, lon)
        },
        async initLocation() {
            if (this.$route.name === 'city' || this.$route.name === 'city-tab') {
                const ok = await this.loadCityFromSlug(this.$route.params.citySlug)
                    .then(() => true).catch(() => false)
                if (ok) return
            }
            const saved = getCookie(LOCATION_COOKIE)
            if (saved) {
                const [lat, lon] = saved.split(',').map(Number)
                await this.loadCityFromCoords(lat, lon).catch(() => {})
                return
            }
            // No saved location — default to Victoria BC silently.
            // Geolocation is opt-in via the "use my location" link in the Locator.
            setCookie(LOCATION_COOKIE, `${DEFAULT_LAT},${DEFAULT_LON}`)
            await this.loadCityFromCoords(DEFAULT_LAT, DEFAULT_LON).catch(() => {})
        },
        buildWikiCandidates(city) {
            const name = city.name || ''
            const region = city.region_name || ''
            const country = city.country_name || ''
            return [
                region  ? `${name}, ${region}`.replace(/ /g, '_') : null,
                country ? `${name}, ${country}`.replace(/ /g, '_') : null,
                name.replace(/ /g, '_'),
            ].filter(Boolean)
        },
        toggleWiki() {
            this.showWiki = !this.showWiki
            if (this.showWiki && !this.wikiData && !this.wikiLoading) {
                this.wikiLoading = true
                this.fetchWiki(this.buildWikiCandidates(this.city))
            }
        },
        fetchWiki(candidates) {
            if (!candidates.length) { this.wikiLoading = false; return }
            const [title, ...rest] = candidates
            fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`)
                .then(r => r.ok ? r.json() : Promise.reject())
                .then(data => {
                    if (data.type === 'disambiguation' || !data.extract) return Promise.reject()
                    this.wikiData = data
                    this.wikiLoading = false
                })
                .catch(() => this.fetchWiki(rest))
        },
    },
    mounted() {
        this.showWiki = getCookie(WIKI_COOKIE) === 'true'
        this.initLocation()
    },
}
</script>

<style></style>
